// Operace okenního frameworku — kombinují lineární kernel (stackId) a WM store (geometrie/z).
//
// Detach přiřadí oknu nový stackId (floating skupina); dock ho zruší (vrátí do plochy);
// navigace z floating okna (openInStack) dědí stackId zdroje → nová vrstva „do linie".
// Z-order je per-stack: focus/detach/dock přenesou celý stack do popředí (raiseStack).

import { useCallback, useMemo } from 'react';

import type { AgendaHandle, NavigationKernel } from '@/shared/navigation/core';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';

import { useWindowManagerStore } from '../core/windowManagerStore';
import {
  PLOCHA_STACK_KEY,
  stackKeyOf,
  type WindowRect,
  type WindowUiState,
} from '../core/windowManagerTypes';
import { readWindowSizeMemory, writeWindowSizeMemory } from '../core/windowSizeMemory';

/** Délka spring animace (ms) — drží krok s `--pwm-anim-dur` (0.2s) + rezerva. */
const ANIM_DUR_MS = 260;

/**
 * Globálně unikátní stackId. Použití `crypto.randomUUID()` (už v repu, např. `sendWindowAction`)
 * je klíčové po reloadu: stackId se obnovují z query (`sid`), modulový čítač by se vynuloval a
 * mohl by vyrobit kolidující id → slévání stacků. UUID kolizi vylučuje.
 */
function nextStackId(): string {
  return `s_${crypto.randomUUID()}`;
}

function membersOfStack(kernel: NavigationKernel, stackId: string): string[] {
  return kernel
    .getState()
    .layers.filter((l) => l.stackId === stackId)
    .map((l) => l.layerId);
}

function countFloating(kernel: NavigationKernel): number {
  return kernel.getState().layers.filter((l) => l.stackId !== undefined).length;
}

/** Rect vrchního okna stacku (poslední v lineárním pořadí, které má rect) — pro překrytí při docku zpět. */
function topRectOfStack(
  kernel: NavigationKernel,
  windows: Readonly<Record<string, WindowUiState>>,
  stackId: string
): WindowRect | undefined {
  const members = kernel.getState().layers.filter((l) => l.stackId === stackId);
  for (let i = members.length - 1; i >= 0; i--) {
    const rect = windows[members[i]?.layerId ?? '']?.rect;
    if (rect) return rect;
  }
  return undefined;
}

export interface WindowingActions {
  focus: (layerId: string) => void;
  detach: (layerId: string) => void;
  dock: (layerId: string) => void;
  minimize: (layerId: string) => void;
  maximize: (layerId: string) => void;
  restore: (layerId: string) => void;
  close: (layerId: string) => void;
  closeGroup: (stackId: string) => void;
  taskClick: (layerId: string) => void;
  openInStack: (
    sourceLayerId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generická agenda handle
    agenda: AgendaHandle<any, any>,
    input: Record<string, unknown>
  ) => Promise<void>;
}

export function useWindowingActions(): WindowingActions {
  const { kernel } = useNavigationContext();
  const wm = useWindowManagerStore;

  // Přenese celý stack daného okna do popředí + nastaví aktivní okno.
  const focus = useCallback(
    (layerId: string) => {
      const layer = kernel.getLayer(layerId);
      const store = wm.getState();
      store.raiseStack(stackKeyOf(layer?.stackId));
      store.setActive(layerId);
    },
    [kernel, wm]
  );

  const detach = useCallback(
    (layerId: string) => {
      const layer = kernel.getLayer(layerId);
      if (!layer) return;
      const store = wm.getState();
      const { w: dw, h: dh } = store.desktopSize;
      const newStackId = nextStackId();
      const n = countFloating(kernel);

      // Osamostatnění ZE stacku → zapamatuj původní stack, aby se dal dock vrátit zpět do něj.
      // Z plochy origin neukládáme (chybějící záznam = dock vrátí na plochu).
      if (layer.stackId) store.setStackOrigin(newStackId, layer.stackId);

      let rect: WindowRect;
      if (layer.stackId) {
        // Už floating → pop ze sub-stacku do vlastní skupiny, lehce odsazené.
        const cur = store.windows[layerId]?.rect;
        const base = cur ?? { x: 90, y: 60, w: Math.round(dw * 0.7), h: Math.round(dh * 0.75) };
        rect = {
          x: Math.max(8, Math.min(dw - 360, base.x + 24)),
          y: Math.max(8, Math.min(dh - 160, base.y + 24)),
          w: base.w,
          h: base.h,
        };
      } else {
        // Docked → nová samostatná floating skupina.
        rect = {
          x: Math.min(Math.max(8, dw - 480), 90 + n * 30),
          y: Math.min(Math.max(8, dh - 300), 60 + n * 30),
          w: Math.round(dw * 0.72),
          h: Math.round(dh * 0.78),
        };
      }

      // localStorage „paměť velikosti per agenda": při RUČNÍM osamostatnění použij zapamatovanou
      // velikost (jen w/h; pozici nech z výpočtu). Po reloadu sem nechodíme — přednost má kotva.
      const remembered = readWindowSizeMemory(layer);
      if (remembered) rect = { ...rect, w: remembered.w, h: remembered.h };

      store.setMode(layerId, 'floating');
      store.setRect(layerId, rect);
      store.setAnim([layerId], 'detach');
      kernel.setLayerStackId(layerId, newStackId);
      wm.getState().raiseStack(newStackId);
      wm.getState().setActive(layerId);
      // Pokud byla agenda naposled maximalizovaná, otevři ji rovnou maximalizovanou
      // (prevRect = zapamatovaná velikost → restore ji vrátí).
      if (remembered?.maximized) wm.getState().maximize(layerId);
      window.setTimeout(() => wm.getState().setAnim([layerId], null), ANIM_DUR_MS);
    },
    [kernel, wm]
  );

  const dock = useCallback(
    (layerId: string) => {
      const layer = kernel.getLayer(layerId);
      if (!layer?.stackId) return;
      const curStackId = layer.stackId;
      const members = membersOfStack(kernel, curStackId);
      const store = wm.getState();
      const origin = store.stackOrigin[curStackId];

      if (origin) {
        // Vrátit do PŮVODNÍHO stacku (zůstává floating). Okna překryjí cílový stack (offset 0),
        // proto převezmou rect jeho vrchního okna.
        const targetRect = topRectOfStack(kernel, store.windows, origin);
        for (const id of members) {
          store.setMode(id, 'floating');
          kernel.setLayerStackId(id, origin);
          if (targetRect) store.setRect(id, targetRect);
        }
        store.setAnim(members, 'dock');
        store.raiseStack(stackKeyOf(origin));
      } else {
        // Vrátit na plochu (osamostatnění bylo z plochy / origin neznámý).
        for (const id of members) {
          store.setMode(id, 'docked');
          kernel.setLayerStackId(id, undefined);
        }
        store.setAnim(members, 'dock');
        store.raiseStack(PLOCHA_STACK_KEY);
      }

      store.setActive(layerId);
      window.setTimeout(() => wm.getState().setAnim(members, null), ANIM_DUR_MS);
    },
    [kernel, wm]
  );

  const minimize = useCallback((layerId: string) => wm.getState().minimize(layerId), [wm]);

  const maximize = useCallback(
    (layerId: string) => {
      wm.getState().maximize(layerId);
      focus(layerId);
      // Zapamatuj „maximalizováno" + velikost před maximalizací (prevRect) pro příští otevření.
      const layer = kernel.getLayer(layerId);
      const prev = wm.getState().windows[layerId]?.prevRect;
      if (layer && prev) writeWindowSizeMemory(layer, { w: prev.w, h: prev.h, maximized: true });
    },
    [kernel, wm, focus]
  );

  const restore = useCallback(
    (layerId: string) => {
      wm.getState().restore(layerId);
      focus(layerId);
      // Po obnovení zapamatuj aktuální (ne-maximalizovanou) velikost.
      const layer = kernel.getLayer(layerId);
      const rect = wm.getState().windows[layerId]?.rect;
      if (layer && rect) writeWindowSizeMemory(layer, { w: rect.w, h: rect.h, maximized: false });
    },
    [kernel, wm, focus]
  );

  const close = useCallback((layerId: string) => void kernel.closeLayer(layerId), [kernel]);

  const closeGroup = useCallback(
    (stackId: string) => {
      const members = membersOfStack(kernel, stackId);
      for (const id of members) void kernel.closeLayer(id);
    },
    [kernel]
  );

  const taskClick = useCallback(
    (layerId: string) => {
      if (wm.getState().windows[layerId]?.mode === 'minimized') wm.getState().restore(layerId);
      focus(layerId);
    },
    [wm, focus]
  );

  const openInStack = useCallback<WindowingActions['openInStack']>(
    async (sourceLayerId, agenda, input) => {
      const src = kernel.getLayer(sourceLayerId);
      await kernel.openModal(agenda, input);
      const newId = kernel.getState().activeTopLayerId;
      if (newId && src?.stackId) {
        kernel.setLayerStackId(newId, src.stackId);
        focus(newId);
      }
    },
    [kernel, focus]
  );

  return useMemo(
    () => ({
      focus,
      detach,
      dock,
      minimize,
      maximize,
      restore,
      close,
      closeGroup,
      taskClick,
      openInStack,
    }),
    [focus, detach, dock, minimize, maximize, restore, close, closeGroup, taskClick, openInStack]
  );
}
